import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';

@registerElement('spin-button')
export default class SpinButtonElement extends ImpulseElement {
  /**
   * The current value.
   */
  @property({ type: Number }) value = 0;

  /**
   * The max value allowed.
   */
  @property({ type: Number }) max = 100;

  /**
   * The min value allowed.
   */
  @property({ type: Number }) min = 0;

  /**
   * The increment/decrement step.
   */
  @property({ type: Number }) step = 0.5;

  /**
   * Whether the control is disabled or not.
   */
  @property({ type: Boolean }) disabled = false;

  @target() output: HTMLElement;

  connected() {
    this.setAttribute('role', 'group');
    this.setAttribute('tabindex', '-1');
    this.output.setAttribute('role', 'spinbutton');
    this.output.setAttribute('aria-valuemax', this.max.toString());
    this.output.setAttribute('aria-valuemin', this.min.toString());
    this.output.setAttribute('aria-valuenow', this.value.toString());
    this.output.setAttribute('aria-valuetext', this.value.toString());
    this.output.setAttribute('aria-live', 'off');
    this.output.setAttribute('value', this.value.toFixed(1));
  }

  valueChanged(value: number) {
    this.output.setAttribute('value', value.toFixed(1));
    this.output.setAttribute('aria-valuenow', value.toString());
    this.output.setAttribute('aria-valuetext', value.toString());
  }

  maxChanged(max: number) {
    this.output.setAttribute('aria-valuemax', max.toString());
  }

  minChanged(min: number) {
    this.output.setAttribute('aria-valuemin', min.toString());
  }

  disabledChanged(value: boolean) {
    this.output.toggleAttribute('disabled', value);
    this.buttons.forEach((button) => {
      button.disabled = value;
    });
  }

  increment() {
    this.value = Math.min(this.value + this.step, this.max);
    this.emit('changed', { detail: { value: this.value } });
  }

  decrement() {
    this.value = Math.max(this.value - this.step, this.min);
    this.emit('changed', { detail: { value: this.value } });
  }

  get buttons(): HTMLButtonElement[] {
    return Array.from(this.querySelectorAll('button'));
  }
}
